import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Descriptions, Tag, Typography, Space, Divider, Spin, Empty, Button, List, Table,
  Modal, Form, Select, Input, message, Segmented, DatePicker, Tooltip,
} from 'antd';
import { FlagFilled, FlagOutlined, FileTextOutlined, PlusOutlined, ExperimentOutlined, BookOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { formatTS } from '../utils/time';
import { UserContext } from '../App';
import ErrorCreateModal from './ErrorCreateModal';
import { useLockConfig, checkLocked } from '../hooks/useLockConfig';

// Renders the AI summary markdown (bold labels + bullet lines) as formatted JSX.
// Handles missing spaces by pattern-matching the known section labels.
function SummaryText({ text }) {
  if (!text) return null;

  const LABELS = [
    'Call Summary',
    'Reason for the call',
    'Is caller a current customer',
    'Actions taken',
    'Issues',
    'Situations agent struggled with',
    'The outcome',
    'Did the agent resolve the inquiry',
  ];

  // Insert missing spaces before known label patterns (source AI bug workaround)
  let fixed = text;
  LABELS.forEach((label) => {
    const compact = label.replace(/\s+/g, '');
    const regex   = new RegExp(compact, 'gi');
    fixed = fixed.replace(regex, label);
  });
  // Also fix common word-run-ons: insert space before capitals that follow lowercase
  fixed = fixed.replace(/([a-z])([A-Z])/g, '$1 $2');

  const lines = fixed.split(/\n/).map((l) => l.trim()).filter(Boolean);

  return (
    <div style={{ lineHeight: 1.65 }}>
      {lines.map((line, i) => {
        // Strip leading "- " or "* "
        const isBullet = /^[-*]\s/.test(line);
        const content  = isBullet ? line.replace(/^[-*]\s+/, '') : line;

        // Render **text** as bold
        const parts = content.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
          /^\*\*.*\*\*$/.test(part)
            ? <strong key={j}>{part.slice(2, -2)}</strong>
            : part
        );

        if (isBullet) {
          return (
            <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <span style={{ color: '#8c8c8c', flexShrink: 0 }}>•</span>
              <span>{parts}</span>
            </div>
          );
        }
        return <div key={i} style={{ marginBottom: 6, fontWeight: 600 }}>{parts}</div>;
      })}
    </div>
  );
}

const EVAL_STATUS_COLOR = {
  assigned: 'blue', in_progress: 'orange', completed: 'green',
  rescore_requested: 'purple', canceled: 'default',
};
const EVAL_STATUS_LABEL = {
  assigned: 'Draft', in_progress: 'In Progress', completed: 'Completed',
  rescore_requested: 'Rescore Requested', canceled: 'Canceled',
};
const SESSION_STATUS_COLOR = { draft: 'default', active: 'blue', completed: 'green', canceled: 'red' };
const SESSION_STATUS_LABEL = { draft: 'Draft', active: 'Active', completed: 'Completed', canceled: 'Canceled' };

function canManageCoaching(user) {
  return (user?.CanEditCoaching ?? 0) >= 2;
}

const { Text, Paragraph } = Typography;

// A phone number on demand. Caller ANI and To Number share one fetch: Show on
// either reveals both, and nothing is fetched until then.
function PhoneNumber({ field, numbers, loading, onShow }) {
  if (numbers) return numbers[field] || '—';
  return <Button size="small" loading={loading} onClick={onShow}>Show</Button>;
}

function descItems(pairs) {
  return pairs
    .filter(([, val]) => val != null && val !== '')
    .map(([label, val], i) => ({ key: i, label, children: val }));
}


function formatDuration(seconds) {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export default function CallDetailView({ detail, loading, contactId, sidePanel = false, onEvalCreated }) {
  const navigate    = useNavigate();
  const currentUser = useContext(UserContext);
  const canEval     = currentUser != null && (currentUser.CanEditEvals ?? 0) >= 2;
  const canError    = currentUser != null && (currentUser.CanEditErrors ?? 0) >= 2;
  const lockConfig  = useLockConfig();

  // Caller ANI / To Number: null until Show; cleared when the call changes.
  const [phones,        setPhones]        = useState(null);
  const [phonesLoading, setPhonesLoading] = useState(false);
  const phoneContactId = detail?.call_log?.ContactID;
  useEffect(() => { setPhones(null); }, [phoneContactId]);
  const showPhones = () => {
    setPhonesLoading(true);
    api.get(`/api/calls/${phoneContactId}/phone-numbers`)
      .then((r) => setPhones(r.data || {}))
      .catch((err) => message.error(err.response?.data?.error || 'Could not load the phone numbers.'))
      .finally(() => setPhonesLoading(false));
  };
  const [evaluations,   setEvaluations]   = useState([]);
  const [evalsLoading,  setEvalsLoading]   = useState(false);
  const [linkedCalls,   setLinkedCalls]    = useState([]);
  const [linkedLoading, setLinkedLoading]  = useState(false);

  // New-evaluation modal state
  const [modalOpen,    setModalOpen]    = useState(false);
  const [modalTitle,   setModalTitle]   = useState('New Evaluation');
  const [users,   setUsers]   = useState([]);
  const [forms,   setForms]   = useState([]);
  const [saving,  setSaving]  = useState(false);
  const [form]                = Form.useForm();

  // New-calibration modal state
  const [calModalOpen,    setCalModalOpen]    = useState(false);
  const [calSaving,       setCalSaving]       = useState(false);
  const [calForm]                             = Form.useForm();
  const [calSessions,     setCalSessions]     = useState([]);
  const [calSessLoading,  setCalSessLoading]  = useState(false);

  // Coaching sessions state
  const [coachSessions,  setCoachSessions]  = useState([]);
  const [coachLoading,   setCoachLoading]   = useState(false);
  const [coachModalOpen, setCoachModalOpen] = useState(false);
  const [coachMode,      setCoachMode]      = useState('new');   // 'new' | 'existing'
  const [coachSaving,    setCoachSaving]    = useState(false);
  const [coachForm]                         = Form.useForm();
  const [draftSessions,  setDraftSessions]  = useState([]);
  const [coachAreas,     setCoachAreas]     = useState([]);
  const [coachReqTypes,  setCoachReqTypes]  = useState([]);
  const [coachCoaches,   setCoachCoaches]   = useState([]);
  const [coachUsers,     setCoachUsers]     = useState([]);
  const [coachInterTypes, setCoachInterTypes] = useState([]);

  // Resolved QA user for this call's agent (null = not found, undefined = not yet resolved)
  const [callAgentUserId,   setCallAgentUserId]   = useState(undefined);
  const [callAgentResolved, setCallAgentResolved] = useState(false);

  // Error submissions linked to this contact
  const [errors,         setErrors]         = useState([]);
  const [errorsLoading,  setErrorsLoading]  = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  // Flag state — initialised from detail, updated optimistically on toggle
  const [userHasFlagged, setUserHasFlagged] = useState(false);
  const [flagCount,      setFlagCount]      = useState(0);

  useEffect(() => {
    setUserHasFlagged(!!detail?.call_log?.UserHasFlagged);
    setFlagCount(detail?.call_log?.FlagCount ?? 0);
  }, [detail]);

  useEffect(() => {
    const agentNo = detail?.call_log?.Agent_No;
    if (!agentNo) {
      setCallAgentUserId(null);
      setCallAgentResolved(true);
      return;
    }
    setCallAgentResolved(false);
    api.get('/api/users', { params: { agent_no: agentNo } })
      .then((r) => {
        setCallAgentUserId(r.data?.[0]?.ID ?? null);
        setCallAgentResolved(true);
      })
      .catch(() => {
        setCallAgentUserId(null);
        setCallAgentResolved(true);
      });
  }, [detail?.call_log?.Agent_No]); // eslint-disable-line

  const handleFlagToggle = async () => {
    try {
      if (userHasFlagged) {
        await api.delete(`/api/flags/${contactId}`, { data: { flag_type: 'flag' } });
        setUserHasFlagged(false);
        setFlagCount((c) => Math.max(0, c - 1));
      } else {
        await api.post(`/api/flags/${contactId}`, { flag_type: 'flag' });
        setUserHasFlagged(true);
        setFlagCount((c) => c + 1);
      }
    } catch {
      message.error('Failed to update flag.');
    }
  };

  const refreshEvals = useCallback(() => {
    if (!contactId) return;
    setEvalsLoading(true);
    // include_feedback_only=1 so both regular and feedback-only evals show on the call detail
    api.get(`/api/cases?contact_id=${contactId}&include_feedback_only=1`)
      .then((res) => setEvaluations(res.data))
      .catch(() => setEvaluations([]))
      .finally(() => setEvalsLoading(false));
  }, [contactId]);

  const _loadUsersAndForms = () => {
    const campaignNo = detail?.call_log?.CampaignNo;
    const campaignId = detail?.call_log?.CampaignID;
    const userParams = { can_edit_evals: 1 };
    if (campaignId) userParams.campaign_id = campaignId;
    Promise.all([
      api.get('/api/users', { params: userParams }),
      api.get('/api/eval-forms/published', { params: campaignNo ? { campaign_no: campaignNo } : {} }),
    ])
      .then(([usersRes, formsRes]) => {
        setUsers(usersRes.data);
        setForms(formsRes.data);
      })
      .catch(() => {});
  };

  const openModal = () => {
    form.resetFields();
    if (currentUser?.ID) form.setFieldValue('assigned_to_user_id', currentUser.ID);
    _loadUsersAndForms();
    setModalTitle('New Evaluation');
    setModalOpen(true);
  };

  const openCalModal = () => {
    calForm.resetFields();
    _loadUsersAndForms();
    setCalModalOpen(true);
  };

  const handleCalCreate = async () => {
    try { await calForm.validateFields(); } catch { return; }
    const values = calForm.getFieldsValue();
    if (!values.evaluator_ids || values.evaluator_ids.length < 2) {
      message.warning('Select at least 2 evaluators.');
      return;
    }
    const cl = detail?.call_log ?? {};
    setCalSaving(true);
    try {
      await api.post('/api/calibration/sessions', {
        title:         values.title,
        contact_id:    contactId,
        form_id:       values.form_id,
        evaluator_ids: values.evaluator_ids,
        call_date:     cl.Date,
        agent_name:    cl.Agent_Name,
        agent_no:      cl.Agent_No,
        campaign_id:   cl.CampaignID,
        skill:         cl.Skill_Name,
        agent_time:    cl.Agent_Time,
      });
      message.success('Calibration session created.');
      setCalModalOpen(false);
      refreshCalSessions();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to create calibration session.');
    } finally {
      setCalSaving(false);
    }
  };

  const handleCreate = async () => {
    const values = form.getFieldsValue();
    const cl = detail?.call_log ?? {};

    if (checkLocked(lockConfig, 'eval', cl.Date)) {
      message.error('This call date is before the configured lock date. Evaluations cannot be created for locked dates.');
      return;
    }

    if (!values.assigned_to_user_id || !values.form_id) {
      message.warning('Please select a user and a form.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/cases', {
        contact_id:          contactId,
        assigned_to_user_id: values.assigned_to_user_id,
        form_id:             values.form_id,
        is_feedback_only:    values.eval_type === 'feedback_only' ? 1 : 0,
        call_date:           cl.Date,
        agent_name:          cl.Agent_Name,
        agent_no:            cl.Agent_No,
        campaign:            cl.Campaign,
        skill:               cl.Skill_Name,
        agent_time:          cl.Agent_Time,
        external_ref:        values.external_ref || null,
      });
      message.success('Evaluation created.');
      setModalOpen(false);
      refreshEvals();
      onEvalCreated?.();
    } catch {
      message.error('Failed to create evaluation.');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => { refreshEvals(); }, [refreshEvals]);

  const refreshErrors = useCallback(() => {
    if (!contactId) return;
    setErrorsLoading(true);
    api.get('/api/errors', { params: { contact_id: contactId } })
      .then((r) => setErrors(r.data))
      .catch(() => setErrors([]))
      .finally(() => setErrorsLoading(false));
  }, [contactId]);

  useEffect(() => { refreshErrors(); }, [refreshErrors]);

  const refreshCalSessions = useCallback(() => {
    if (!contactId) return;
    setCalSessLoading(true);
    api.get('/api/calibration/sessions', { params: { contact_id: contactId } })
      .then((r) => setCalSessions(r.data))
      .catch(() => setCalSessions([]))
      .finally(() => setCalSessLoading(false));
  }, [contactId]);

  useEffect(() => { refreshCalSessions(); }, [refreshCalSessions]);

  const refreshCoachingSessions = useCallback(() => {
    if (!contactId) return;
    setCoachLoading(true);
    api.get(`/api/coaching/sessions/for-contact/${contactId}`)
      .then((res) => setCoachSessions(res.data))
      .catch(() => setCoachSessions([]))
      .finally(() => setCoachLoading(false));
  }, [contactId]);

  useEffect(() => { refreshCoachingSessions(); }, [refreshCoachingSessions]);

  const openCoachModal = async () => {
    setCoachMode('new');
    coachForm.resetFields();
    try {
      const coachParams = {};
      if (detail?.call_log?.CampaignID) coachParams.campaign_id = detail.call_log.CampaignID;
      const [areasRes, coachesRes, usersRes, typesRes, draftsRes, reqTypesRes] = await Promise.all([
        api.get('/api/coaching/areas'),
        api.get('/api/coaching/coaches', { params: coachParams }),
        api.get('/api/coaching/active-users'),
        api.get('/api/coaching/interaction-types'),
        api.get('/api/coaching/sessions', { params: { status: 'draft' } }),
        api.get('/api/coaching/request-types'),
      ]);
      setCoachAreas(areasRes.data);
      setCoachReqTypes(reqTypesRes.data || []);
      setCoachCoaches(coachesRes.data);
      setCoachUsers(usersRes.data);
      setCoachInterTypes(typesRes.data);
      setDraftSessions(draftsRes.data);
    } catch { message.error('Failed to load options.'); return; }
    coachForm.setFieldsValue({
      coach_user_id:   currentUser?.ID ?? null,
      participant_ids: callAgentUserId ? [callAgentUserId] : [],
    });
    setCoachModalOpen(true);
  };

  const handleCoachSave = async () => {
    let values;
    try { values = await coachForm.validateFields(); }
    catch { return; }

    setCoachSaving(true);
    try {
      if (coachMode === 'new') {
        await api.post('/api/coaching/sessions', {
          title:           values.title,
          campaign_id:     call_log?.CampaignID || null,
          area_id:         values.area_id,
          request_type_id: values.request_type_id,
          coach_user_id:   values.coach_user_id || null,
          due_date:        values.due_date ? values.due_date.format('YYYY-MM-DD') : null,
          participant_ids: values.participant_ids || [],
          interactions: [{ contact_id: String(contactId), type_id: values.type_id || null, note: values.inter_note || null }],
        });
        message.success('Coaching session created.');
      } else {
        // Fetch current interactions then append
        const sessionRes = await api.get(`/api/coaching/sessions/${values.session_id}`);
        const existing = (sessionRes.data.Interactions || []).map((i) => ({
          contact_id: i.ContactID, note: i.Note, type_id: i.TypeID || null,
        }));
        await api.put(`/api/coaching/sessions/${values.session_id}`, {
          interactions: [...existing, { contact_id: String(contactId), type_id: values.type_id || null, note: values.inter_note || null }],
        });
        message.success('Added to coaching session.');
      }
      setCoachModalOpen(false);
      refreshCoachingSessions();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to save.');
    } finally { setCoachSaving(false); }
  };

  useEffect(() => {
    if (!detail?.call_log?.MasterContactID) return;
    setLinkedLoading(true);
    api.get(`/api/calls/linked/${detail.call_log.MasterContactID}`, { params: { exclude: contactId } })
      .then((res) => setLinkedCalls(res.data))
      .catch(() => setLinkedCalls([]))
      .finally(() => setLinkedLoading(false));
  }, [detail?.call_log?.MasterContactID]);

  if (loading) return <Spin style={{ display: 'block', margin: '40px auto' }} />;
  if (!detail) return <Empty description="No data" />;

  const { call_log, summary, ai_data, reasons, outcomes, topics, transcript, flags, interaction_id, segment_id } = detail;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">

      {/* ── Top action bar ────────────────────────────────────────────────── */}
      {(!sidePanel || currentUser?.Role !== 'agent') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {interaction_id ? (
            <Typography.Link
              href={`https://na1.nice-incontact.com/player/#/cxone-player/interactions/${interaction_id}?acd-contacts=${call_log?.MasterContactID}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              🎧 Play Interaction
            </Typography.Link>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              🎧 No interaction recording found
            </Typography.Text>
          )}
          {segment_id ? (
            <Typography.Link
              href={`https://na1.nice-incontact.com/player/#/cxone-player/segments/${segment_id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              🎧 Play Segment
            </Typography.Link>
          ) : currentUser?.Role !== 'agent' ? (
            <Typography.Link
              href={`https://na1.nice-incontact.com/player/#/cxone-player/acd-contacts/${contactId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              🎧 ACD Recording Link
            </Typography.Link>
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              🎧 No segment recording found
            </Typography.Text>
          )}
          {currentUser?.Role !== 'agent' && (
            <Tooltip title={
              userHasFlagged
                ? `You flagged this${flagCount > 1 ? ` (+${flagCount - 1} others)` : ''} — click to remove`
                : flagCount > 0
                  ? `${flagCount} user(s) flagged this — click to add yours`
                  : 'Flag this call'
            }>
              <Button
                size="small"
                onClick={handleFlagToggle}
                icon={
                  userHasFlagged
                    ? <FlagFilled style={{ color: '#ff4d4f' }} />
                    : flagCount > 0
                      ? <FlagFilled style={{ color: '#faad14' }} />
                      : <FlagOutlined style={{ color: '#bfbfbf' }} />
                }
              >
                {flagCount > 0 ? `${flagCount} Flag${flagCount === 1 ? '' : 's'}` : 'Flag'}
              </Button>
            </Tooltip>
          )}
        </div>
      )}

      {contactId && (
        <>
          <Divider>
            <Space>
              Evaluations
              {callAgentResolved && !callAgentUserId ? (
                <Typography.Text style={{ color: '#ff4d4f', fontSize: 12 }}>
                  {detail?.call_log?.Agent_No ? 'No QA user account found for this agent' : 'No agent on this call'}
                </Typography.Text>
              ) : (
                <>
                  {/* canEval only. This button always opens the evaluation
                      modal and posts to /api/cases, so showing it to someone
                      with error rights alone offered them a create they are
                      not allowed (the API rejects it with a 403). Users with
                      error rights create from the Create Error Log button
                      under the Errors divider below. */}
                  {canEval && callAgentUserId && (
                    <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openModal}>
                      New Evaluation
                    </Button>
                  )}
                  {canEval && callAgentUserId && (
                    <Button size="small" icon={<ExperimentOutlined />} onClick={openCalModal}>
                      New Calibration
                    </Button>
                  )}
                </>
              )}
            </Space>
          </Divider>

          {evalsLoading ? (
            <Spin size="small" />
          ) : evaluations.length === 0 ? (
            <Typography.Text type="secondary">No evaluations assigned to this call.</Typography.Text>
          ) : (
            <List
              size="small"
              dataSource={evaluations}
              renderItem={(ev) => (
                <List.Item
                  actions={[
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      onClick={() => navigate(`/cases/${ev.ID}`)}
                    >
                      Open
                    </Button>,
                  ]}
                >
                  <Space>
                    <Tag color={EVAL_STATUS_COLOR[ev.Status] ?? 'default'}>
                      {EVAL_STATUS_LABEL[ev.Status] ?? ev.Status}
                    </Tag>
                    {ev.IsFeedbackOnly ? <Tag color="purple">Feedback Only</Tag> : null}
                    <Typography.Text>
                      {ev.FormName ? `${ev.FormName}${ev.FormVersion ? ` v${ev.FormVersion}` : ''}` : 'No form'}
                    </Typography.Text>
                    {ev.AssignedToName && (
                      <Typography.Text type="secondary">— {ev.AssignedToName}</Typography.Text>
                    )}
                  </Space>
                </List.Item>
              )}
            />
          )}

          <Modal maskClosable={false}
            title="New Calibration Session"
            open={calModalOpen}
            onOk={handleCalCreate}
            onCancel={() => setCalModalOpen(false)}
            confirmLoading={calSaving}
            okText="Create"
          >
            <Form form={calForm} layout="vertical" style={{ marginTop: 16 }}>
              {call_log?.Campaign && (
                <Form.Item label="Campaign">
                  <Input disabled value={call_log.Campaign} />
                </Form.Item>
              )}
              <Form.Item label="Session Title" name="title" rules={[{ required: true, message: 'Required' }]}>
                <Input placeholder="e.g. May 2026 Calibration" />
              </Form.Item>
              <Form.Item label="Evaluation Form" name="form_id" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select a form…"
                  options={forms.map((f) => ({ label: `${f.Name} (v${f.Version})`, value: f.ID }))}
                  notFoundContent="No published forms available."
                />
              </Form.Item>
              <Form.Item label="Evaluators (min 2)" name="evaluator_ids" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select evaluators…"
                  options={users.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))}
                />
              </Form.Item>
            </Form>
          </Modal>

          <Modal maskClosable={false}
            title={modalTitle}
            open={modalOpen}
            onOk={handleCreate}
            onCancel={() => setModalOpen(false)}
            confirmLoading={saving}
            okText="Create"
          >
            <Form form={form} layout="vertical" style={{ marginTop: 16 }}
              initialValues={{ eval_type: 'evaluation' }}>
              <Form.Item label="Type" name="eval_type" required>
                <Segmented options={[
                  { label: 'Evaluation',    value: 'evaluation' },
                  { label: 'Feedback Only', value: 'feedback_only' },
                ]} />
              </Form.Item>
              <Form.Item label="Assign to" name="assigned_to_user_id" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch optionFilterProp="label" placeholder="Select a user…"
                  options={users.map((u) => ({ label: u.DisplayName || u.Email, value: u.ID }))}
                />
              </Form.Item>
              <Form.Item label="Evaluation Form" name="form_id" rules={[{ required: true, message: 'Required' }]}>
                <Select
                  showSearch optionFilterProp="label" placeholder="Select a form…"
                  options={forms.map((f) => ({ label: `${f.Name} (v${f.Version})`, value: f.ID }))}
                  notFoundContent="No published forms available."
                />
              </Form.Item>
              <Form.Item label="External Ref" name="external_ref">
                <Input placeholder="Optional reference number or ID…" allowClear />
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}

      {/* ── Calibration Sessions ──────────────────────────────────────────── */}
      {contactId && calSessions.length > 0 && (
        <>
          <Divider>
            <Space>
              <ExperimentOutlined />
              Calibration Sessions
            </Space>
          </Divider>
          {calSessLoading ? (
            <Spin size="small" />
          ) : (
            <List
              size="small"
              dataSource={calSessions}
              renderItem={(cs) => {
                const statusLabel = cs.CompletedCount === cs.TotalEvaluators && cs.TotalEvaluators > 0
                  ? 'Complete'
                  : cs.CompletedCount > 0 ? 'In Progress' : 'Pending';
                const statusColor = statusLabel === 'Complete' ? 'green' : statusLabel === 'In Progress' ? 'orange' : 'default';
                return (
                  <List.Item
                    actions={[
                      <Button size="small" icon={<FileTextOutlined />}
                        onClick={() => navigate(`/calibration/${cs.ID}`)}>
                        Open
                      </Button>,
                    ]}
                  >
                    <Space>
                      <Tag color={statusColor}>{statusLabel}</Tag>
                      <Typography.Text>{cs.Title}</Typography.Text>
                      {cs.FormName && (
                        <Typography.Text type="secondary">
                          — {cs.FormName}{cs.FormVersion ? ` v${cs.FormVersion}` : ''}
                        </Typography.Text>
                      )}
                    </Space>
                  </List.Item>
                );
              }}
            />
          )}
        </>
      )}

      {/* ── Error Submissions ─────────────────────────────────────────────── */}
      {contactId && (
        <>
          <ErrorCreateModal
            open={errorModalOpen}
            agentUserId={callAgentUserId}
            agentName={detail?.call_log?.Agent_Name ?? null}
            campaignNo={detail?.call_log?.CampaignNo ?? null}
            initialCampaign={detail?.call_log?.Campaign ?? null}
            initialCampaignId={detail?.call_log?.CampaignID ?? null}
            initialAssignedToId={currentUser?.ID}
            contactId={contactId}
            callDate={detail?.call_log?.Date ?? null}
            currentUser={currentUser}
            navigate={navigate}
            onClose={() => setErrorModalOpen(false)}
            onCreated={() => { setErrorModalOpen(false); refreshErrors(); }}
          />
          <Divider>
            <Space>
              <WarningOutlined style={{ color: '#ff4d4f' }} />
              Errors
              {canError && (
                callAgentResolved && !callAgentUserId
                  ? <Typography.Text style={{ color: '#ff4d4f', fontSize: 12 }}>No QA user account found for this agent</Typography.Text>
                  : callAgentUserId && (
                    <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setErrorModalOpen(true)}>
                      Create Error Log
                    </Button>
                  )
              )}
            </Space>
          </Divider>
          {errorsLoading ? (
            <Spin size="small" />
          ) : errors.length === 0 ? (
            <Typography.Text type="secondary">No error submissions linked to this call.</Typography.Text>
          ) : (
            <List
              size="small"
              dataSource={errors}
              renderItem={(er) => (
                <List.Item
                  actions={[
                    <Button size="small" icon={<FileTextOutlined />} onClick={() => navigate(`/errors/${er.ID}`)}>
                      Open
                    </Button>,
                  ]}
                >
                  <Space>
                    <Tag color={
                      er.Status === 'completed' ? 'green'
                      : er.Status === 'rescore_requested' ? 'purple'
                      : er.Status === 'in_progress' ? 'orange' : 'default'
                    }>
                      {er.Status === 'rescore_requested' ? 'Disputed'
                       : er.Status === 'in_progress' ? 'In Progress'
                       : er.Status === 'completed' ? 'Completed' : er.Status}
                    </Tag>
                    {er.TotalErrors > 0 && <Tag color="red">{er.TotalErrors} error{er.TotalErrors !== 1 ? 's' : ''}</Tag>}
                    <Typography.Text>{er.FormName}</Typography.Text>
                    {er.SubmittedByName && <Typography.Text type="secondary">— {er.SubmittedByName}</Typography.Text>}
                  </Space>
                </List.Item>
              )}
            />
          )}
        </>
      )}

      {/* ── Coaching Sessions ─────────────────────────────────────────────── */}
      {contactId && (
        <>
          <Divider>
            <Space>
              Coaching Sessions
              {canManageCoaching(currentUser) && (
                callAgentResolved && !callAgentUserId
                  ? <Typography.Text style={{ color: '#ff4d4f', fontSize: 12 }}>No QA user account found for this agent</Typography.Text>
                  : callAgentUserId && (
                    <Button size="small" type="primary" icon={<BookOutlined />} onClick={openCoachModal}>
                      Add to Coaching Session
                    </Button>
                  )
              )}
            </Space>
          </Divider>

          {coachLoading ? (
            <Spin size="small" />
          ) : coachSessions.length === 0 ? (
            <Typography.Text type="secondary">No coaching sessions linked to this call.</Typography.Text>
          ) : (
            <List
              size="small"
              dataSource={coachSessions}
              renderItem={(cs) => (
                <List.Item
                  actions={[
                    <Button size="small" onClick={() => navigate(`/coaching/sessions/${cs.ID}`)}>Open</Button>,
                  ]}
                >
                  <Space wrap>
                    <Tag color={SESSION_STATUS_COLOR[cs.Status]}>{SESSION_STATUS_LABEL[cs.Status] ?? cs.Status}</Tag>
                    <Typography.Text strong>{cs.Title}</Typography.Text>
                    {cs.AreaName && <Typography.Text type="secondary">— {cs.AreaName}</Typography.Text>}
                    {cs.TypeName && <Tag>{cs.TypeName}</Tag>}
                    {cs.InteractionNote && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{cs.InteractionNote}</Typography.Text>}
                  </Space>
                </List.Item>
              )}
            />
          )}

          <Modal maskClosable={false}
            title="Add to Coaching Session"
            open={coachModalOpen}
            onOk={handleCoachSave}
            onCancel={() => setCoachModalOpen(false)}
            confirmLoading={coachSaving}
            okText={coachMode === 'new' ? 'Create Session' : 'Add to Session'}
            width={560}
            destroyOnClose
          >
            <Segmented
              block
              value={coachMode}
              onChange={(v) => { setCoachMode(v); coachForm.resetFields(); }}
              options={[
                { label: 'New Session',             value: 'new'      },
                { label: 'Add to Existing Draft',   value: 'existing' },
              ]}
              style={{ marginBottom: 16, marginTop: 8 }}
            />

            <Form form={coachForm} layout="vertical">
              {/* ── New session fields ── */}
              {coachMode === 'new' && (
                <>
                  {call_log?.Campaign && (
                    <Form.Item label="Campaign">
                      <Input disabled value={call_log.Campaign} />
                    </Form.Item>
                  )}
                  <Form.Item label="Title" name="title" rules={[{ required: true, message: 'Title is required' }]}>
                    <Input placeholder="e.g. Communication Skills Coaching" />
                  </Form.Item>
                  <Form.Item label="Request Type" name="request_type_id" rules={[{ required: true, message: 'Request Type is required' }]}>
                    <Select showSearch optionFilterProp="label" placeholder="Select a request type…"
                      options={coachReqTypes.map((t) => ({ label: t.Name, value: t.ID }))} />
                  </Form.Item>
                  <Form.Item label="Area" name="area_id" rules={[{ required: true, message: 'Area is required' }]}>
                    <Select showSearch optionFilterProp="label" placeholder="Select an area…"
                      options={coachAreas.map((a) => ({ label: a.Name, value: a.ID }))} />
                  </Form.Item>
                  <Form.Item label="Due Date" name="due_date">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item label="Coach" name="coach_user_id" rules={[{ required: true, message: 'Required' }]}>
                    <Select showSearch optionFilterProp="label" placeholder="Assign a coach…"
                      options={coachCoaches.map((c) => ({ label: c.DisplayName, value: c.ID }))} />
                  </Form.Item>
                  <Form.Item label="Participants" name="participant_ids"
                    rules={[{ required: true, message: 'At least one participant is required' }]}>
                    <Select mode="multiple" showSearch optionFilterProp="label" placeholder="Select participants…"
                      options={coachUsers.map((u) => ({ label: u.DisplayName, value: u.ID }))} />
                  </Form.Item>
                </>
              )}

              {/* ── Existing session field ── */}
              {coachMode === 'existing' && (
                <Form.Item label="Session" name="session_id"
                  rules={[{ required: true, message: 'Please select a session' }]}
                  extra={draftSessions.length === 0 ? 'No draft sessions available.' : null}>
                  <Select showSearch optionFilterProp="label" placeholder="Select a draft session…"
                    options={draftSessions.map((s) => ({ label: s.Title, value: s.ID }))} />
                </Form.Item>
              )}

              {/* ── Interaction fields (shared) ── */}
              <Form.Item label="Interaction Type" name="type_id">
                <Select allowClear placeholder="Select a type (optional)"
                  options={coachInterTypes.map((t) => ({ label: t.Name, value: t.ID }))} />
              </Form.Item>
              <Form.Item label="Comment" name="inter_note">
                <Input.TextArea rows={2} placeholder="Optional comment about this interaction…" />
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}

      {call_log && (
        <Descriptions
          title="Call Info"
          bordered
          size="small"
          column={2}
          items={descItems([
            ['Contact ID',        call_log.ContactID],
            ['Master Contact ID', call_log.MasterContactID ?? '—'],
            ['Date',              call_log.Date],
            ['Time',          call_log.Time],
            ['Campaign',      call_log.Campaign],
            ['Skill',         call_log.Skill_Name],
            ['Agent',         call_log.Agent_Name],
            ['Disposition',   call_log.Disp_Name],
            ['Media',         call_log.Media_Name],
            ['Direction',     call_log.Direction],
            ['Contact Type',  call_log.ContactType],
            ['End Reason',    call_log.ContactEndReason],
            ['Talk Time',     call_log.Agent_Time != null ? `${call_log.Agent_Time}s` : null],
            ['ACW Time',      call_log.ACW_Time   != null ? `${call_log.ACW_Time}s`   : null],
            ['Hold Time',     call_log.Hold_Time  != null ? `${call_log.Hold_Time}s`  : null],
            ['ANI Called Back Qty', call_log.ANICalledBackQty != null ? String(call_log.ANICalledBackQty) : null],
            // Evaluations Edit only, and hidden until asked for: the number
            // is fetched (and the view logged) on Show.
            ...((currentUser?.CanEditEvals ?? 0) >= 2
              ? [
                  ['Caller ANI', <PhoneNumber key="ani" field="caller_ani" numbers={phones} loading={phonesLoading} onShow={showPhones} />],
                  ['To Number',  <PhoneNumber key="to"  field="to_number"  numbers={phones} loading={phonesLoading} onShow={showPhones} />],
                ]
              : []),
            ['Called Support',      call_log.CalledSupport != null ? (call_log.CalledSupport ? 'Yes' : 'No') : null],
            ['Called Tier 2',       call_log.CalledTier2   != null ? (call_log.CalledTier2   ? 'Yes' : 'No') : null],
          ])}
        />
      )}

      {call_log?.MasterContactID && (
        <>
          <Divider>Calls w/Same Master Contact ID</Divider>
          <Table
            rowKey="ContactID"
            size="small"
            loading={linkedLoading}
            dataSource={linkedCalls}
            pagination={false}
            scroll={{ x: true }}
            rowClassName={(r) => r.ContactID === call_log.ContactID ? 'linked-call-current' : ''}
            onRow={(r) => ({
              onClick: () => r.ContactID !== call_log.ContactID && navigate(`/calls/${r.ContactID}`),
              style: { cursor: r.ContactID !== call_log.ContactID ? 'pointer' : 'default' },
            })}
            columns={[
              {
                title: 'Contact ID', dataIndex: 'ContactID', key: 'ContactID', width: 110,
                render: (v, r) => r.ContactID === call_log.ContactID
                  ? <><Text strong>{v}</Text> <Tag style={{ fontSize: 11 }}>This call</Tag></>
                  : <Typography.Link>{v}</Typography.Link>,
              },
              { title: 'Date',      dataIndex: 'Date',       key: 'Date',      width: 100 },
              { title: 'Direction', dataIndex: 'Direction',  key: 'Direction', width: 90 },
              { title: 'Agent',     dataIndex: 'Agent_Name', key: 'Agent_Name', ellipsis: true },
              {
                title: 'Talk Time', dataIndex: 'Agent_Time', key: 'Agent_Time', width: 80,
                render: formatDuration,
              },
            ]}
          />
        </>
      )}

      {summary && (
        <Descriptions
          title="AI Summary"
          bordered
          size="small"
          column={1}
          labelStyle={{ width: 110, whiteSpace: 'nowrap' }}
          items={[
            ...descItems([
              ['Resolved', summary.IsResolved],
              ['Stars',    summary.Stars],
              ['City',     summary.City],
              ['State',    summary.State],
              ['Zip',      summary.Zip],
            ]),
            ...(summary.Summary ? [{
              key: 'summary-text',
              label: 'Summary',
              children: <SummaryText text={summary.Summary} />,
            }] : []),
          ]}
        />
      )}

      {reasons?.length > 0 && (
        <>
          <Text strong>AI Reason(s)</Text>
          {reasons.map((r, i) => (
            <Descriptions
              key={i}
              bordered
              size="small"
              column={1}
              style={{ marginBottom: 8 }}
              items={descItems([
                ['Reason',      r.Reason],
                ['Sub-Reason',  r.SubReason],
                ['Explanation', r.ReasonExplanation],
              ])}
            />
          ))}
        </>
      )}

      {outcomes?.length > 0 && (
        <>
          <Text strong>AI Outcome(s)</Text>
          {outcomes.map((o, i) => (
            <Descriptions
              key={i}
              bordered
              size="small"
              column={1}
              style={{ marginBottom: 8 }}
              items={descItems([
                ['Outcome',     o.Outcome],
                ['Explanation', o.OutcomeExplanation],
              ])}
            />
          ))}
        </>
      )}

      {topics?.length > 0 && (
        <>
          <Text strong>AI Topic(s)</Text>
          {topics.map((t, i) => (
            <Descriptions
              key={i}
              bordered
              size="small"
              column={1}
              style={{ marginBottom: 8 }}
              items={descItems([
                ['Category',    t.Category],
                ['Topic',       t.Topic],
                ['Description', t.Description],
              ])}
            />
          ))}
        </>
      )}

      {ai_data && (
        <Descriptions title="AI Data" bordered size="small" column={1} labelStyle={{ width: 180, whiteSpace: 'nowrap' }} items={[
          { key: 'SentimentStart',            label: 'Sentiment Start',          children: ai_data.SentimentStart            ?? '—' },
          { key: 'SentimentEnd',              label: 'Sentiment End',            children: ai_data.SentimentEnd              ?? '—' },
          { key: 'SentimentChange',           label: 'Sentiment Change',         children: ai_data.SentimentChange           ?? '—' },
          { key: 'FeedbackAgent',             label: 'Agent Feedback',           children: ai_data.FeedbackAgent             ?? '—' },
          { key: 'FeedbackAgentCategory',     label: 'Agent Feedback Category',  children: ai_data.FeedbackAgentCategory     ?? '—' },
          { key: 'FeedbackAgentExplanation',  label: 'Agent Feedback Note',      children: ai_data.FeedbackAgentExplanation  ?? '—' },
          { key: 'FeedbackCompany',           label: 'Company Feedback',         children: ai_data.FeedbackCompany           ?? '—' },
          { key: 'FeedbackCompanyCategory',   label: 'Company Feedback Category',children: ai_data.FeedbackCompanyCategory   ?? '—' },
          { key: 'FeedbackCompanyExplanation',label: 'Company Feedback Note',    children: ai_data.FeedbackCompanyExplanation?? '—' },
          { key: 'EscOrTransfer',             label: 'Escalated / Transferred',  children: ai_data.EscOrTransfer             ?? '—' },
          { key: 'EscOrTransferParty',        label: 'Escalation Party',         children: ai_data.EscOrTransferParty        ?? '—' },
          { key: 'EscOrTransferExplanation',  label: 'Escalation Note',          children: ai_data.EscOrTransferExplanation  ?? '—' },
          { key: 'SaveAttemptApplicable',     label: 'Save Applicable',          children: ai_data.SaveAttemptApplicable     ?? '—' },
          { key: 'SaveAttemptMade',           label: 'Save Attempt Made',        children: ai_data.SaveAttemptMade           ?? '—' },
          { key: 'SaveAttemptOutcome',        label: 'Save Outcome',             children: ai_data.SaveAttemptOutcome        ?? '—' },
          { key: 'SaveAttemptDescription',    label: 'Save Description',         children: ai_data.SaveAttemptDescription    ?? '—' },
          { key: 'UpsellingAttempted',        label: 'Upsell Attempted',         children: ai_data.UpsellingAttempted        ?? '—' },
          { key: 'UpsellCircumstance',        label: 'Upsell Circumstance',      children: ai_data.UpsellCircumstance        ?? '—' },
          { key: 'UpsellHasPolicy',           label: 'Upsell Has Policy',        children: ai_data.UpsellHasPolicy           ?? '—' },
          { key: 'UpsellExplanation',         label: 'Upsell Note',              children: ai_data.UpsellExplanation         ?? '—' },
          { key: 'SurveyMentioned',           label: 'Survey Mentioned',         children: ai_data.SurveyMentioned           ?? '—' },
          { key: 'SurveyResponse',            label: 'Survey Response',          children: ai_data.SurveyResponse            ?? '—' },
          { key: 'AudioIssues',               label: 'Audio Issues',             children: ai_data.AudioIssues               ?? '—' },
          { key: 'AudioIssuesParty',          label: 'Audio Issues Party',       children: ai_data.AudioIssuesParty          ?? '—' },
          { key: 'AudioIssuesExplanation',    label: 'Audio Issues Note',        children: ai_data.AudioIssuesExplanation    ?? '—' },
          { key: 'EndedUnexpectedly',         label: 'Ended Unexpectedly',       children: ai_data.EndedUnexpectedly         ?? '—' },
          { key: 'IsFollowUp',                label: 'Is Follow-Up',             children: ai_data.IsFollowUp                ?? '—' },
        ]} />
      )}

      {transcript && (
        <>
          <Divider>Transcript</Divider>
          <Paragraph
            style={{
              background: '#fafafa',
              border: '1px solid #d9d9d9',
              borderRadius: 6,
              padding: 16,
              whiteSpace: 'pre-wrap',
              maxHeight: 400,
              overflowY: 'auto',
              fontSize: 13,
            }}
          >
            {transcript}
          </Paragraph>
        </>
      )}

      {flags?.length > 0 && (
        <>
          <Divider>Flags</Divider>
          <Space wrap>
            {flags.map((f, i) => (
              <Tag key={i} icon={<FlagFilled style={{ color: '#ff4d4f' }} />} color="default">
                {f.FlaggedByName || f.Email} — {formatTS(f.FlaggedAt)}
              </Tag>
            ))}
          </Space>
        </>
      )}

    </Space>
  );
}
